// pages/inventory/StoreReceiptListing.jsx
// ✅ Converted from: StoreReceiptListing.aspx + StoreReceiptListing.aspx.cs
// ✅ Approval logic preserved (Node handles full InsertStoreReceiptApproval transaction)
// ✅ NO rights logic (excluded as instructed)
// ✅ Includes pagination + autocomplete + validations

import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../../components/HeaderBar";
import LayoutWrapper from "../../../components/LayoutWrapper";
import getCurrentSkin from "../../../utils/getCurrentSkin";
import { ThemeContext } from "../../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Building2, Search, Plus, RefreshCcw, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

// ---------- helpers ----------
const s = (v) => (v ?? "").toString().trim();
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

// ISO (YYYY-MM-DD) -> Date
const isoToDate = (iso) => {
  const x = s(iso);
  if (!x) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(x)) {
    const d = new Date(`${x}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

// dd/MM/yyyy -> Date
const ddmmyyyyToDate = (dmy) => {
  const x = s(dmy);
  if (!x) return null;
  const m = x.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const dd = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const yyyy = parseInt(m[3], 10);
  const d = new Date(yyyy, mm - 1, dd);
  return Number.isNaN(d.getTime()) ? null : d;
};

// dd/MM/yyyy -> ISO (YYYY-MM-DD)
const dmyToIso = (dmy) => {
  const d = ddmmyyyyToDate(dmy);
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// ISO -> dd/MM/yyyy
const isoToDmy = (iso) => {
  const d = isoToDate(iso);
  if (!d) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

// today at 00:00
const today00 = () => {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
};

// add days to ISO date string
const isoAddDays = (iso, days) => {
  const d = isoToDate(iso) || today00();
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const StoreReceiptListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // session-like (match infra usage)
  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const loginUserId = localStorage.getItem("userId") || "";
  // ASP Session["financeyearstartdate"] dd/MM/yyyy
  const financeYearStartDate = localStorage.getItem("financeYearStartDate") || "";

  // UI toggles
  const [showFilters, setShowFilters] = useState(true);

  // filters (ASP defaults on first load: from=today-7, to=today)
  const [fromDate, setFromDate] = useState(""); // ISO
  const [toDate, setToDate] = useState(""); // ISO
  const [receiptNo, setReceiptNo] = useState("");
  const [receiptType, setReceiptType] = useState("Select"); // Select | P | R | W

  // autocomplete
  const [acOpen, setAcOpen] = useState(false);
  const [acItems, setAcItems] = useState([]);
  const [acLoading, setAcLoading] = useState(false);

  // grid
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // approval selection map: { receiptId: true/false }
  const [selected, setSelected] = useState({});

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const screenBadge = useMemo(() => "INFRA SCREEN : A-32/1", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  // ---------- validations ----------
  // matches ASP: Check_from_to + checkDate rule (finance-year start)
  const validateDates = () => {
    const t = today00();
    const fd = isoToDate(fromDate);
    const td = isoToDate(toDate);

    if (fd && fd > t) {
      alert("Select from date lesser than current date ");
      return false;
    }
    if (td && td > t) {
      alert("Select to date lesser than or Equal to Current date ");
      return false;
    }
    if (fd && td && td < fd) {
      alert("Please select to date less than from date");
      return false;
    }

    const fy = ddmmyyyyToDate(financeYearStartDate);
    if (fy) {
      if (fd && fd < fy) {
        alert(`You can select only Greater than or Equal to Financial Year Date ${financeYearStartDate}`);
        return false;
      }
      if (td && td < fy) {
        alert(`You can select only Greater than or Equal to Financial Year Date ${financeYearStartDate}`);
        return false;
      }
    }
    return true;
  };

  // ---------- API ----------
  const fetchList = async (p = page) => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(branchId)) {
      toast.error("Missing login info (branchId).");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/inventory/store-receipt-listing/list`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          receiptType,
          receiptNo: receiptNo.trim(),
          // Node expects dd/MM/yyyy (to match SP call)
          fromDate: fromDate ? isoToDmy(fromDate) : "",
          toDate: toDate ? isoToDmy(toDate) : "",
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        const list = res.data.list || [];
        setRows(list);
        setTotal(res.data.totalCount || 0);
        setPage(res.data.page || p);

        // reset selections after bind (GridView rebind behavior)
        setSelected({});
      } else {
        toast.error(res?.data?.message || "Failed to load Store Receipt listing.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchAutocomplete = async (q) => {
    const query = s(q);
    if (!query) {
      setAcItems([]);
      setAcOpen(false);
      return;
    }
    if (!s(finYearId) || !s(branchId)) return;

    try {
      setAcLoading(true);

      const res = await axios.get(`${BASE_URL}/api/inventory/store-receipt-listing/autocomplete`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          receiptType,
          prefix: query,
        },
      });

      if (res?.data?.success) {
        setAcItems(res.data.suggestions || []);
        setAcOpen(true);
      } else {
        setAcItems([]);
        setAcOpen(false);
      }
    } catch {
      setAcItems([]);
      setAcOpen(false);
    } finally {
      setAcLoading(false);
    }
  };

const approveSelected = async () => {
  const ids = Object.entries(selected)
    .filter(([, v]) => !!v)
    .map(([k]) => toInt(k))
    .filter((x) => x > 0); // reject 0

  if (ids.length === 0) {
    alert("Please select any One");
    return;
  }

    if (!s(finYearId) || !s(branchId)) {
      toast.error("Missing Financial Year / Branch.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${BASE_URL}/api/inventory/store-receipt-listing/approve`, {
        finYearId: Number(finYearId),
        branchId: Number(branchId),
        loginUserId: toInt(loginUserId), // kept for parity; Node doesn't need it here
        receiptIds: ids,
      });

      if (res?.data?.success) {
        const msg = res?.data?.message || "Done";
        alert(msg); // ASP uses MessageBox alert
        await fetchList(page);
      } else {
        toast.error(res?.data?.message || "Please Try Again");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || "Please Try Again";
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  // ---------- events ----------
  const handleSearch = async (e) => {
    e?.preventDefault?.();
    if (!validateDates()) return;
    setPage(1);
    await fetchList(1);
  };

  const handleRefresh = async () => {
    await fetchList(page);
  };

  const handleAddNew = () => {
    // ASP: Response.Redirect("StoreReceiptNote.aspx")
    window.location.href = "/inventory/StoreReceiptNote";
  };

  const handleEdit = (r) => {
    const id = toInt(r?.fld_StoreReceiptId ?? r?.fld_storereceiptid);
    if (!id) return;

    // ASP: Session["ReceiptID"]=id then redirect to StoreReceiptNote.aspx
    // React: pass id in querystring (entry page can load by receiptId)
    window.location.href = `/inventory/StoreReceiptNote?receiptId=${id}`;
  };

  // ---------- init ----------
  useEffect(() => {
    // defaults like ASP !IsPostBack
    const todayIso = new Date().toISOString().slice(0, 10);
    setFromDate(isoAddDays(todayIso, -7));
    setToDate(todayIso);

    // initial list
    setTimeout(() => fetchList(1), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // when page changes, load that page
  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  const pageButtons = useMemo(() => {
    const tp = Math.max(1, totalPages);
    const win = 7;
    let start = Math.max(1, page - Math.floor(win / 2));
    let end = start + win - 1;
    if (end > tp) {
      end = tp;
      start = Math.max(1, end - win + 1);
    }
    const arr = [];
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  }, [page, totalPages]);

  return (
    <LayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">STORE RECEIPT LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Store Receipt Listing
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Filter / Search / Approve / Pagination
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleRefresh} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleAddNew} className={primaryBtn} disabled={loading}>
                <Plus className="w-4 h-4" />
                Add New
              </button>

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
              {/* From / To */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label className={labelBase}>From Date</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputBase + " mt-2"}
                    max={new Date().toISOString().slice(0, 10)}
                  />
                </div>

                <div>
                  <label className={labelBase}>To Date</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputBase + " mt-2"}
                    max={new Date().toISOString().slice(0, 10)}
                  />
                </div>
              </div>

              {/* Receipt No + Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                <div className="relative">
                  <label className={labelBase}>Receipt No</label>
                  <input
                    value={receiptNo}
                    onChange={(e) => {
                      const v = e.target.value;
                      setReceiptNo(v);
                      fetchAutocomplete(v);
                    }}
                    onBlur={() => setTimeout(() => setAcOpen(false), 120)}
                    onFocus={() => {
                      if (acItems.length > 0) setAcOpen(true);
                    }}
                    autoComplete="off"
                    placeholder="Type Receipt No..."
                    className={inputBase + " mt-2"}
                  />

                  {acOpen && (
                    <div
                      className={`absolute z-50 mt-2 w-full rounded-2xl border shadow-xl overflow-hidden ${
                        isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"
                      }`}
                    >
                      {acLoading ? (
                        <div className={`px-4 py-3 text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
                          Loading...
                        </div>
                      ) : acItems.length === 0 ? (
                        <div className={`px-4 py-3 text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                          No suggestions
                        </div>
                      ) : (
                        acItems.map((x, idx) => (
                          <button
                            type="button"
                            key={`${x}-${idx}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setReceiptNo(x);
                              setAcOpen(false);
                            }}
                            className={`w-full text-left px-4 py-3 text-sm hover:${
                              isDark ? "bg-white/10" : "bg-gray-50"
                            } ${isDark ? "text-gray-200" : "text-gray-800"}`}
                          >
                            {x}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className={labelBase}>Receipt Type</label>
                  <select
                    value={receiptType}
                    onChange={(e) => {
                      setReceiptType(e.target.value);
                      setReceiptNo("");
                      setAcItems([]);
                      setAcOpen(false);
                    }}
                    className={inputBase + " mt-2"}
                  >
                    <option value="Select">Select</option>
                    <option value="P">Direct Receipt</option>
                    <option value="R">Issue Returned</option>
                    <option value="W">From Workshop</option>
                  </select>
                </div>
              </div>

              {/* Search button */}
              <div className="flex justify-center">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </form>
          )}

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Receipt No</th>
                    <th className="px-4 py-4 text-center font-bold">Date</th>
                    <th className="px-4 py-4 text-center font-bold">Type</th>
                    <th className="px-4 py-4 text-left font-bold">Amount</th>
                    <th className="px-4 py-4 text-center font-bold">Approval</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 6 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
rows.map((r, idx) => {
  const id = toInt(
    r.fld_StoreReceiptID ??
    r.fld_StoreReceiptId ??
    r.fld_storereceiptid ??
    r.fld_StoreReceiptid ??
    0
  );

  // debug once if needed
  // console.log("row id", id, r);

  const approvalText = s(r.Approved ?? r.fld_IsApproved ?? "");
  const isApproved =
    approvalText === "Approved" ||
    approvalText === "1" ||
    approvalText === "true" ||
    r.fld_IsApproved === 1 ||
    r.fld_IsApproved === true;

                      return (
                        <tr
                          key={`${id}-${idx}`}
                          className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            <button
                              type="button"
                              onClick={() => handleEdit(r)}
                              className="font-bold text-blue-600 hover:underline"
                              title="Edit"
                            >
                              {r.fld_branchwiseno}
                            </button>
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_Date}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_ReceiptType}
                          </td>

                          <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_TotalValue}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
  <div className="flex items-center justify-center gap-3">
    <span className={`${isApproved ? "text-emerald-500 font-bold" : "text-gray-500"}`}>
      {approvalText || "Pending"}
    </span>

    {!isApproved && (
      <input
        type="checkbox"
        // Use a stable unique key per row
        checked={!!selected[String(id || `row-${idx}`)]}
        onChange={(e) => {
          const key = String(id || `row-${idx}`);
          // Never allow selecting invalid id 0 if real ID is missing
          if (!id) {
            toast.warn("This receipt has no valid ID and cannot be approved.");
            return;
          }
          setSelected((prev) => ({
            ...prev,
            [key]: e.target.checked,
          }));
        }}
      />
    )}

    {isApproved && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
  </div>
</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Approve footer button (like GridView FooterTemplate) */}
            <div className={`p-5 flex justify-center border-t ${isDark ? "border-white/10" : "border-gray-200"}`}>
              <button onClick={approveSelected} className={primaryBtn} disabled={loading}>
                <CheckCircle2 className="w-4 h-4" />
                Approve
              </button>
            </div>
          </div>

          {/* Bottom row: pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="flex flex-wrap items-center gap-3">
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {pageButtons[0] > 1 && (
                    <>
                      <button
                        onClick={() => setPage(1)}
                        className="px-4 py-2 text-sm font-bold hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      >
                        1
                      </button>
                      {pageButtons[0] > 2 && (
                        <span className={`px-3 py-2 text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>…</span>
                      )}
                    </>
                  )}

                  {pageButtons.map((pno) => (
                    <button
                      key={pno}
                      onClick={() => setPage(pno)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        page === pno
                          ? `${primaryBtnClass} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {pno}
                    </button>
                  ))}

                  {pageButtons[pageButtons.length - 1] < totalPages && (
                    <>
                      {pageButtons[pageButtons.length - 1] < totalPages - 1 && (
                        <span className={`px-3 py-2 text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>…</span>
                      )}
                      <button
                        onClick={() => setPage(totalPages)}
                        className="px-4 py-2 text-sm font-bold hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Small note about FY date usage */}
          {financeYearStartDate && (
            <div className={`mt-6 text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              Financial Year Start Date: <b>{financeYearStartDate}</b>
            </div>
          )}
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default StoreReceiptListing;